import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useApi } from "~hooks/apiHooks/useApi";
import { useInvalidateCache } from "~hooks/useInvalidateCache.ts";
import { useUpdatePermissionScopeCache } from "~hooks/useUpdatePermissionScopeCache.ts";
import type { DefaultOperationSuccessResponse } from "~types/response";

const useDeleteCompetition = () => {
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const request = useApi();
  const { mutateAsync: invalidateCache } = useInvalidateCache();
  const updatePermissionScopeCache = useUpdatePermissionScopeCache();

  return useMutation<DefaultOperationSuccessResponse, Error, string>({
    mutationFn: async (competitionId: string) => {
      const url = `${sport}/o/${organizationId}/competitions/${competitionId}`;
      return await request(url, {
        method: "DELETE",
      });
    },
    onSuccess: async (response, competitionId) => {
      const operationResult = response?.data?.[0];

      if (!operationResult?.success) {
        return;
      }

      updatePermissionScopeCache(sport, organizationId, (updatedScope) => {
        updatedScope.scope.leagueTree = updatedScope.scope.leagueTree.map((league) => ({
          ...league,
          competitions: league.competitions.filter((competition) => competition.competitionId !== competitionId),
        }));

        if (updatedScope.resources?.competitions?.[competitionId]) {
          delete updatedScope.resources.competitions[competitionId];
        }
      });

      await Promise.all([
        invalidateCache(),
        queryClient.invalidateQueries({ queryKey: ["competitions", sport, organizationId, competitionId] }),
      ]);
    },
  });
};

export default useDeleteCompetition;
